/* ============================================================
   ErrorBoundary — a throw in any lazy screen used to blank the whole app.
   Mounted around the route outlet in AppLayout, keyed by pathname so simply
   navigating away resets it.

   Stale chunks: after a redeploy an open tab still asks for the old hashed
   chunk. nginx answers that miss with a reload stub (nginx.conf @stale_js), and
   when the stub does not reload, lazy() resolves to its module — which has no
   default export — so React throws #306. Re-rendering cannot fix that; only a
   full reload fetches the new index.html. Recovery reloads once per 30s, on the
   same sessionStorage key the stub uses, so a genuinely missing chunk cannot
   loop.
   ============================================================ */

import { Component } from 'react'
import { EmptyState } from '../ui/primitives'

const CHUNK_RELOAD_KEY = 'tf-chunk-reload'
const CHUNK_RELOAD_WINDOW_MS = 30000

/** A lazy screen whose chunk could not be loaded (stale deploy / network). */
function isChunkError(error) {
  const message = String(error?.message || '')
  return /Minified React error #306|Lazy element type must resolve|dynamically imported module|Importing a module script failed|Failed to load module script/i
    .test(message)
}

/** Reload unless one already ran in the last 30s. Returns whether it reloaded. */
function reloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0
    if (Date.now() - last <= CHUNK_RELOAD_WINDOW_MS) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

export default class ErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    if (isChunkError(error) && reloadOnce()) return
    console.error('Screen crashed:', error, info?.componentStack)
  }

  retry = () => {
    // A stale chunk stays stale on re-render — fetch the new build instead.
    if (isChunkError(this.state.error)) window.location.reload()
    else this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      const stale = isChunkError(this.state.error)
      return (
        <div className="page">
          <EmptyState icon="alert" title={stale ? 'A new version is available' : 'Something went wrong'}>
            {stale
              ? 'This screen was updated since the page was opened. Reload to load the latest version.'
              : 'This screen hit an unexpected error. The rest of the app is fine — try again, or head back to the dashboard.'}
            {/* The raw error ("Cannot read properties of undefined…") means nothing
                to a user; componentDidCatch already logs it with the stack. */}
          </EmptyState>
          <div style={{ textAlign: 'center' }}>
            <button className="btn btn-secondary" onClick={this.retry}>
              {stale ? 'Reload' : 'Try again'}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
